import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Card from "../../../theme/untitled/Card/Card";
import Icon from "../../../theme/untitled/icons/Icon";
import Badge from "../../../theme/untitled/Badge/Badge";
import LoadingIndicator from "../../../../calendar/components/LoadingIndicator";
import SourceRow from "./SourceRow";
import { useIntegrationsStore } from "../../../../../hooks/redux/integrations";
import { useImportsStore } from "../../../../../hooks/redux/imports";
import { useCurrentOrganizationStore } from "../../../../../hooks/redux/currentOrganization";
import { IT } from "../../../../../consts";
import { getSourceLevel, getSourceName, formatRelativeTime, formatNumber } from "./utils";
import "./CalendarSourcesWidget.css";

const CALENDAR_SOURCE_TYPES = [IT.google, IT.outlook];

/**
 * Replaces the old "Recent integration syncs" run table on the dashboard —
 * one row per calendar source (not per sync run), per
 * assets/handoffs/design_handoff_integrations_widget.
 *
 * Two things the original design assumes but the app doesn't have yet, so
 * they're left out rather than faked:
 * - No "Stale" status — no time-based staleness concept exists anywhere in
 *   the sync/integration logic (only healthy vs. an error family). See
 *   utils.js.
 * - No "Events synced this month" usage band, and no per-row event count —
 *   the imports API has no aggregate and only returns one paginated page,
 *   so neither figure can be computed honestly (only the plain total run
 *   count, used in the footer, comes from real pagination data).
 */
const CalendarSourcesWidget = () => {
  const { currentOrganization } = useCurrentOrganizationStore();
  const { integrations, fetchIntegrations } = useIntegrationsStore();
  const { imports, fetchImports } = useImportsStore();

  // Same fix as WelcomeScreen: usage is populated the moment checkSession
  // resolves, so gating on it (rather than integrations.collection, which
  // only fills in after this widget's own fetch below) avoids a
  // show-then-hide flash on load.
  const hasIntegration = (currentOrganization.model?.usage?.integrations || 0) > 0;

  useEffect(() => {
    if (hasIntegration) {
      fetchIntegrations();
      fetchImports(1, 1);
    }
  }, [hasIntegration]);

  if (!hasIntegration) {
    return null;
  }

  const sources = integrations.collection.filter((i) =>
    CALENDAR_SOURCE_TYPES.includes(i.type)
  );
  const isLoading = integrations.fetch && !integrations.fetchSuccess;
  // Removing the last source deletes its sync history — this is the same
  // "no source" state as the initial one, not a separate case (see README).
  const hasSources = sources.length > 0;
  const failedSource = sources.find((i) => getSourceLevel(i.status) === "failed");

  const body = isLoading ? (
    <LoadingIndicator />
  ) : hasSources ? (
    <div className="csw-rows">
      {sources.map((integration) => (
        <SourceRow key={integration.uuid} integration={integration} />
      ))}
    </div>
  ) : (
    <div className="csw-empty">
      <Badge color="gray">Not connected</Badge>
      <p className="csw-empty-copy">
        No calendar source is connected, so nothing is syncing. Set up an
        integration with Google Calendar™ or Outlook Calendar to start
        pulling events.
      </p>
      <Link to="/integrations" className="csw-btn-secondary">
        Set up integration
      </Link>
    </div>
  );

  const footer =
    !isLoading && hasSources ? (
      failedSource ? (
        <div className="csw-footer">
          <span>
            Events from {getSourceName(failedSource)} stopped updating{" "}
            {formatRelativeTime(failedSource.last_sync)}.
          </span>
        </div>
      ) : (
        <div className="csw-footer">
          {imports.pagination?.count != null && (
            <span>{formatNumber(imports.pagination.count)} runs in total.</span>
          )}
        </div>
      )
    ) : null;

  return (
    <Card>
      <Card.Header
        title="Integrations"
        subtitle="Data sources feeding your widgets."
      >
        <Link to="/integrations" className="csw-tertiary-link">
          Manage
          <Icon name="ArrowNarrowRight" size={16} />
        </Link>
      </Card.Header>
      <Card.Body noPadding>{body}</Card.Body>
      {footer}
    </Card>
  );
};

export default CalendarSourcesWidget;
